import { colors } from 'components/src/foundation';
import ArrowLeft from 'components/src/svg/ArrowLeft';
import { Subheading } from 'components/src/typography';
import * as React from 'react';
import FormTabs from './FormTabs';
import { ContactFormFieldsFragment, ImageWithFocalPointFieldsFragment, Maybe } from 'types/generated';
import TextWithInlineColor from '../../TextWithInlineColor';
import { ImageWithFocalPoint } from '../../ImageWithFocalPoint';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Document, BLOCKS } from '@contentful/rich-text-types';
import { HeadingType } from '@/types';
import Button from 'components/src/button';
import VideoMedia from '@/components/common/VideoMedia';

type Props = {
  data: ContactFormFieldsFragment;
  onClose: () => void;
  contactFormSetting?: Maybe<string>;
};

const renderImage = (
  imageData: {
    image: ImageWithFocalPointFieldsFragment | null | undefined;
    imageText: HeadingType;
    media?: {
      __typename?: 'Asset';
      title?: string | null;
      description?: string | null;
      contentType?: string | null;
      fileName?: string | null;
      size?: number | null;
      url?: string | null;
      width?: number | null;
      height?: number | null;
    } | null;
  } | null
) => {
  if (imageData?.media && imageData.media.url) {
    return <VideoMedia id={`contact-media-${imageData.media.title}`} url={imageData.media.url} fadeDirection="none" />;
  }
  if (imageData?.image && imageData?.imageText) {
    return (
      <div className="relative h-[540px] md:h-full ">
        <ImageWithFocalPoint image={imageData.image} priority sizes={{ breakpoints: { md: '50vw' } }} />
        <div className="absolute bottom-[10%] z-50 mx-32 text-white-100">
          {documentToReactComponents(imageData.imageText as Document, {
            renderNode: {
              [BLOCKS.HEADING_6]: (_node, children) => (
                <Subheading classes="mb-24 text-black-100">{children}</Subheading>
              ),
            },
          })}
        </div>
      </div>
    );
  }
  return null;
};

const DoBusinessWithUsForm = ({ data, onClose, contactFormSetting }: Props) => {
  const [selectedTab, setSelectedTab] = React.useState(0);

  React.useEffect(() => {
    if (contactFormSetting === 'Company') {
      setSelectedTab(1);
    } else {
      setSelectedTab(0);
    }
  }, [contactFormSetting]);

  let homeMediaData = null;
  const homeTabContent = data.formTabsCollection?.items?.[0]?.content;
  if (homeTabContent?.__typename === 'Form') {
    homeMediaData = {
      image: homeTabContent.image,
      imageText: homeTabContent.imageText?.json,
      media: homeTabContent.media,
    };
  }

  let companyMediaData = null;
  const companyTabContent = data.formTabsCollection?.items?.[1]?.content;
  if (companyTabContent?.__typename === 'Form') {
    companyMediaData = {
      image: companyTabContent.image,
      imageText: companyTabContent.imageText?.json,
      media: companyTabContent.media,
    };
  }

  return (
    <div className="grid max-h-[950px] grid-cols-12">
      <div
        className={
          'col-span-full rounded-28 bg-white-100 px-32 py-32 md:col-span-6 md:rounded-l-28 md:rounded-r-0 md:px-64'
        }
      >
        <Button
          label="Til baka"
          variant="text"
          bordered
          icon={<ArrowLeft color={colors.black[75]} />}
          onClick={onClose}
        />
        <div className="mt-32">
          <TextWithInlineColor
            heading={data.title?.text?.json}
            headingVariant="large"
            textColor={'neutral-10'}
            color={data.title?.color}
            classes="!text-lg-fixed md:!text-2xl-fixed"
          />
          {data.formTabsCollection?.items && (
            <FormTabs
              tabs={data.formTabsCollection?.items}
              selectedTab={selectedTab}
              onSetSelectedTab={setSelectedTab}
            />
          )}
        </div>
      </div>
      <div className="col-span-full hidden h-full md:col-span-6 md:block">
        <div
          id="media-image-0"
          className={`${
            selectedTab === 0 ? 'block' : 'hidden'
          } brand-fade sticky top-0 h-full overflow-hidden rounded-r-28`}
        >
          {renderImage(homeMediaData)}
          <div className="brand-fade relative top-0 z-10 h-full w-full"></div>
        </div>
        <div
          id="media-image-1"
          className={`${
            selectedTab === 1 ? 'block' : 'hidden'
          } sticky top-0 h-full overflow-hidden overscroll-none rounded-r-28`}
        >
          {renderImage(companyMediaData)}
          <div className="brand-fade relative top-0 z-10 h-full w-full"></div>
        </div>
      </div>
    </div>
  );
};

export default DoBusinessWithUsForm;
